import React, { useCallback, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import ActionLoader from '../../components/ActionLoader';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../../context/ThemeContext';
import { getThemeColors } from '../../utils/themeStyles';
import { useAuth } from '../../context/AuthContext';
import { orgApi, OrgInvitation } from '../../api/org';
import { useT } from '../../i18n/useT';

export default function MyInvitationsScreen() {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { getAccessToken } = useAuth();
  const [items, setItems] = useState<OrgInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) { setLoading(false); return; }
    try { const r = await orgApi.myInvitations(token); setItems(r.invitations ?? []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
  }, [getAccessToken]);

  useFocusEffect(useCallback(() => { setLoading(true); load(); }, [load]));

  const accept = async (inv: OrgInvitation) => {
    if (!inv.token) return;
    setBusy(inv.token); setMsg(null);
    try {
      await orgApi.acceptInvitation(getAccessToken(), inv.token);
      setMsg(t('invitation.joined', { name: inv.company_name }));
      await load();
    } catch { setMsg(t('invitation.acceptFailed')); }
    finally { setBusy(null); }
  };

  const decline = async (inv: OrgInvitation) => {
    if (!inv.token) return;
    setBusy(inv.token); setMsg(null);
    try {
      await orgApi.declineInvitation(getAccessToken(), inv.token);
      setMsg(t('invitation.declined', { name: inv.company_name }));
      await load();
    } catch { setMsg(t('invitation.declineFailed')); }
    finally { setBusy(null); }
  };

  return (
    <SafeAreaView style={[s.container, { backgroundColor: colors.background }]}>
      <ScrollView contentContainerStyle={s.scroll}>
        {loading && <View style={{ marginTop: 20 }}><ActionLoader label={t('invitation.list.loading')} /></View>}
        {msg && <Text style={[s.msg, { color: colors.success }]}>{msg}</Text>}
        {!loading && items.length === 0 && (
          <Text style={[s.empty, { color: colors.textTertiary }]}>{t('invitation.list.none')}</Text>
        )}
        {items.map((inv) => (
          <View key={inv.invitation_id} style={[s.card, { backgroundColor: colors.cardBackground }]}>
            <Ionicons name="mail" size={22} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <Text style={[s.name, { color: colors.textPrimary }]}>{inv.company_name}</Text>
              <Text style={[s.sub, { color: colors.textSecondary }]}>{t('invitation.list.subtitle')}</Text>
            </View>
            <View style={{ gap: 6 }}>
              <TouchableOpacity onPress={() => accept(inv)} disabled={busy === inv.token}
                style={[s.acceptBtn, { backgroundColor: colors.primary }]}>
                <Text style={s.acceptText}>{busy === inv.token ? '…' : t('invitation.join')}</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => decline(inv)} disabled={busy === inv.token}
                style={[s.declineBtn, { borderColor: colors.border }]}>
                <Text style={[s.declineText, { color: colors.textSecondary }]}>{t('invitation.decline')}</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  container: { flex: 1 },
  scroll: { padding: 20 },
  msg: { fontSize: 13, marginBottom: 12 },
  empty: { fontSize: 14, marginTop: 20, textAlign: 'center' },
  card: { flexDirection: 'row', alignItems: 'center', gap: 12, borderRadius: 12, padding: 14, marginBottom: 10 },
  name: { fontSize: 15, fontWeight: '700' },
  sub: { fontSize: 12, marginTop: 2 },
  acceptBtn: { paddingHorizontal: 16, paddingVertical: 9, borderRadius: 8, alignItems: 'center' },
  acceptText: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  declineBtn: { paddingHorizontal: 16, paddingVertical: 7, borderRadius: 8, borderWidth: 1, alignItems: 'center' },
  declineText: { fontWeight: '600', fontSize: 12 },
});
